import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Info, Loader2, Mail } from "lucide-react";
import {
  AuthCard,
  AuthCardPage,
  AuthField,
  BackToSignIn,
  authInputClass,
  authPrimaryButton,
} from "@/components/auth/AuthLayout";

const RESEND_SECONDS = 60;

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [wait, setWait] = useState(0);
  const { error: toastError } = useToast();

  useEffect(() => {
    if (wait <= 0) return;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const send = async (address: string) => {
    setSubmitting(true);
    try {
      await api.post("/api/auth/forgot-password", { email: address });
      setSentTo(address);
      setWait(RESEND_SECONDS);
    } catch (err: any) {
      toastError("Request failed", err.message || "Unable to send the reset email.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toastError("Email needed", "Enter the email you sign in with.");
      return;
    }
    send(email.trim());
  };

  if (sentTo) {
    const domain = sentTo.split("@")[1]?.toLowerCase() || "";
    const webmail = domain.includes("gmail") ? "https://mail.google.com" : /outlook|hotmail|live/.test(domain) ? "https://outlook.live.com/mail" : null;
    return (
      <AuthCardPage topRight={<BackToSignIn />}>
        <AuthCard
          tone="success"
          status="Email sent"
          title="Check your email"
          subtitle={
            <>
              If <strong className="text-foreground font-semibold">{sentTo}</strong> has an account, a reset link is on its way. It works
              once, and expires in 30 minutes.
            </>
          }
        >
          {webmail && (
            <a href={webmail} target="_blank" rel="noreferrer" className="h-12 rounded-xl border border-border text-sm font-medium flex items-center justify-center hover:bg-muted">
              Open your inbox
            </a>
          )}
          <div className="pt-3.5 border-t border-border space-y-2 text-[13.5px] text-muted-foreground">
            <div>
              Nothing yet? Look in Spam, or{" "}
              <button
                type="button"
                disabled={wait > 0 || submitting}
                onClick={() => send(sentTo)}
                className="font-medium text-primary hover:underline disabled:text-muted-foreground disabled:no-underline"
              >
                {wait > 0 ? (
                  <>
                    resend in{" "}
                    <span className="font-mono">
                      {Math.floor(wait / 60)}:{String(wait % 60).padStart(2, "0")}
                    </span>
                  </>
                ) : (
                  "send it again"
                )}
              </button>
            </div>
            <div>
              Wrong address?{" "}
              <button type="button" onClick={() => setSentTo(null)} className="font-medium text-primary hover:underline">
                Use a different email
              </button>
            </div>
          </div>
        </AuthCard>
      </AuthCardPage>
    );
  }

  return (
    <AuthCardPage topRight={<BackToSignIn />}>
      <form onSubmit={handleSubmit} className="w-full flex justify-center">
        <AuthCard
          title="Forgot your password?"
          subtitle="Enter the work email on your account and we’ll send you a secure link to choose a new password."
          className="gap-[18px]"
        >
          <AuthField id="email" label="Work email" icon={<Mail />}>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@nelgates.com"
              autoComplete="email"
              autoFocus
              className={authInputClass}
              required
            />
          </AuthField>
          <button type="submit" disabled={submitting} className={authPrimaryButton}>
            {submitting ? (
              <>
                <Loader2 className="size-4 animate-spin" /> Sending…
              </>
            ) : (
              "Send reset link"
            )}
          </button>
          <div className="flex gap-2.5 rounded-xl bg-muted/60 px-3.5 py-3 text-[12.5px] text-muted-foreground">
            <Info className="size-4 shrink-0 mt-px" />
            <span>
              No access to that inbox? Your admin can send you a fresh set-up link from Settings › Users. Back to{" "}
              <Link to="/login" className="font-medium text-primary hover:underline">
                sign in
              </Link>
              .
            </span>
          </div>
        </AuthCard>
      </form>
    </AuthCardPage>
  );
}
